/* DAILE v2 (dv) — dependency-free core. Exposes window.DV.
 *
 * Registry
 *   DV.register(name, init)   init(root) is called once per root: every
 *                             .shopify-section (and [data-dv-root] outside
 *                             sections) on DOMContentLoaded, then again for
 *                             each section the theme editor (re)loads.
 *                             init may return a cleanup function, or an object
 *                             { destroy, onBlockSelect(block, event),
 *                               onBlockDeselect(block, event),
 *                               onSectionSelect(event), onSectionDeselect(event) }.
 *   DV.init(root) / DV.destroy(root)   run / tear down all modules manually.
 *
 * Generic behaviours (no section-specific logic)
 *   [data-dv-dialog-open="id"]  opens <dialog id="id"> modally; [data-dv-close]
 *                               closes the closest dialog; Escape, backdrop
 *                               click, focus return, body scroll lock.
 *   dialog[data-dv-close-on-link]  closes when a link inside it is clicked.
 *   details[data-dv-autoclose]  closes on outside click / Escape.
 *   video[data-dv-loop-video]   lazy <source data-src>, plays only while
 *                               visible, [data-dv-motion-toggle] pause/play,
 *                               reduced motion => manual start.
 *   html.dv-page-scrolled       after 24px of scroll (top mist).
 *   DV.toast(message)           polite status toast.
 */
(() => {
  'use strict';

  if (window.DV && window.DV.version) return;

  const doc = document;
  const html = doc.documentElement;
  const ROOTS = '.shopify-section, [data-dv-root]';
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  const modules = new Map();          // name -> init
  const instances = new WeakMap();    // root -> Map(name -> handle)
  let started = false;

  const queryAll = (scope, selector) => {
    if (!scope) return [];
    const found = scope.querySelectorAll ? [...scope.querySelectorAll(selector)] : [];
    if (scope.matches && scope.matches(selector)) found.unshift(scope);
    return found;
  };

  const onMediaChange = (mql, fn) => {
    if (mql.addEventListener) mql.addEventListener('change', fn);
    else if (mql.addListener) mql.addListener(fn);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', fn);
      else if (mql.removeListener) mql.removeListener(fn);
    };
  };

  const normalise = (value) => {
    if (typeof value === 'function') return { destroy: value };
    if (value && typeof value === 'object') return value;
    return {};
  };

  const report = (name, error) => {
    if (window.console && console.error) console.error(`[DV] module "${name}" failed:`, error);
  };

  /* ---------------------------------------------------------------- roots */

  const allRoots = () => {
    const roots = [...doc.querySelectorAll('.shopify-section')];
    doc.querySelectorAll('[data-dv-root]').forEach((el) => {
      if (!el.closest('.shopify-section')) roots.push(el);
    });
    return roots;
  };

  const runModule = (root, name, init) => {
    let map = instances.get(root);
    if (!map) {
      map = new Map();
      instances.set(root, map);
    }
    if (map.has(name)) return;
    try {
      map.set(name, normalise(init(root)));
    } catch (error) {
      map.set(name, {});
      report(name, error);
    }
  };

  const init = (root) => {
    if (!root) return;
    modules.forEach((fn, name) => runModule(root, name, fn));
  };

  const destroy = (root) => {
    const map = root && instances.get(root);
    if (map) {
      map.forEach((handle, name) => {
        try {
          if (typeof handle.destroy === 'function') handle.destroy();
        } catch (error) {
          report(name, error);
        }
      });
      instances.delete(root);
    }
    syncScrollLock();
  };

  const register = (name, fn) => {
    if (typeof name !== 'string' || typeof fn !== 'function') return;
    if (modules.has(name)) return; // script evaluated twice (theme editor)
    modules.set(name, fn);
    if (started) allRoots().forEach((root) => runModule(root, name, fn));
  };

  const dispatchHook = (root, hook, ...args) => {
    const map = root && instances.get(root);
    if (!map) return;
    map.forEach((handle, name) => {
      if (typeof handle[hook] !== 'function') return;
      try {
        handle[hook](...args);
      } catch (error) {
        report(name, error);
      }
    });
  };

  /* -------------------------------------------------------------- dialogs */

  let dialogOpener = null;
  const triggers = new WeakMap(); // dialog -> trigger element
  const managed = new WeakSet();  // dialogs opened through DV

  const syncScrollLock = () => {
    html.classList.toggle('dv-scroll-lock', !!doc.querySelector('dialog.dv-dialog[open]'));
  };
  // Round 7: dialogs opened without DV (showModal() in a section script)
  // lock the page too — this replaces html:has(dialog.dv-dialog[open]) in
  // dv-base.css. Only `open` attributes are watched.
  if ('MutationObserver' in window) {
    new MutationObserver((records) => {
      if (records.some((record) => record.target instanceof HTMLDialogElement && record.target.classList.contains('dv-dialog'))) syncScrollLock();
    }).observe(doc.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
  }

  const resolveDialog = (target) => {
    if (!target) return null;
    const el = typeof target === 'string' ? doc.getElementById(target) : target;
    return el && typeof el.showModal === 'function' ? el : null;
  };

  const openDialog = (target, trigger) => {
    const dialog = resolveDialog(target);
    if (!dialog) return null;
    const active = doc.activeElement;
    if (!active || !active.closest || !active.closest('dialog')) dialogOpener = trigger || active;
    doc.querySelectorAll('dialog.dv-dialog[open]').forEach((other) => {
      if (other !== dialog) other.close();
    });
    managed.add(dialog);
    if (!dialog.open) dialog.showModal();
    if (trigger) {
      triggers.set(dialog, trigger);
      if (trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
    }
    syncScrollLock();
    dialog.dispatchEvent(new CustomEvent('dv:dialog:open', { bubbles: true, detail: { trigger: trigger || null } }));
    return dialog;
  };

  const closeDialog = (target) => {
    const dialog = resolveDialog(target);
    if (dialog && dialog.open) dialog.close();
  };

  const onDialogClosed = (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!managed.has(dialog) && !dialog.classList.contains('dv-dialog')) return;
    const trigger = triggers.get(dialog);
    if (trigger && trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
    triggers.delete(dialog);
    syncScrollLock();
    // `close` is dispatched asynchronously: if another dialog opened meanwhile, keep focus there.
    if (!doc.querySelector('dialog[open]') && dialogOpener && dialogOpener.isConnected) {
      dialogOpener.focus({ preventScroll: true });
      dialogOpener = null;
    }
    dialog.dispatchEvent(new CustomEvent('dv:dialog:close', { bubbles: true }));
  };

  const onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    doc.querySelectorAll('details[data-dv-autoclose][open]').forEach((details) => {
      if (!details.contains(target)) details.open = false;
    });

    const opener = target.closest('[data-dv-dialog-open]');
    if (opener) {
      const dialog = resolveDialog(opener.getAttribute('data-dv-dialog-open'));
      if (dialog) {
        event.preventDefault();
        openDialog(dialog, opener);
        return;
      }
    }

    const closer = target.closest('[data-dv-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) {
        event.preventDefault();
        dialog.close();
        return;
      }
    }

    // Backdrop click: the event target is the dialog itself, outside its box.
    if (target instanceof HTMLDialogElement && target.open && target.classList.contains('dv-dialog')) {
      const r = target.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) {
        target.close();
        return;
      }
    }

    const link = target.closest('a[href]');
    if (link) {
      const host = link.closest('dialog[data-dv-close-on-link][open]');
      if (host) host.close();
    }
  };

  const onDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;
    doc.querySelectorAll('details[data-dv-autoclose][open]').forEach((details) => {
      const hadFocus = details.contains(doc.activeElement);
      details.open = false;
      const summary = details.querySelector('summary');
      if (hadFocus && summary) summary.focus();
    });
  };

  /* ------------------------------------------------------ scroll + toast */

  let scrollQueued = false;
  const syncScroll = () => {
    html.classList.toggle('dv-page-scrolled', (window.scrollY || html.scrollTop) > 24);
    scrollQueued = false;
  };
  const onScroll = () => {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(syncScroll);
  };

  let toastEl = null;
  let toastTimer = 0;
  const ensureToast = () => {
    if (toastEl && toastEl.isConnected) return toastEl;
    toastEl = doc.createElement('div');
    toastEl.className = 'dv dv-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    doc.body.append(toastEl);
    return toastEl;
  };
  const toast = (message, options = {}) => {
    const el = ensureToast();
    const timeout = typeof options.timeout === 'number' ? options.timeout : 3200;
    el.textContent = String(message == null ? '' : message);
    requestAnimationFrame(() => el.classList.add('dv-toast--visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('dv-toast--visible'), timeout);
  };

  /* ---------------------------------------------------- theme editor */

  const sectionOf = (el) => (el && el.closest ? el.closest(ROOTS) : null);

  const bindEditor = () => {
    doc.addEventListener('shopify:section:load', (event) => init(event.target));
    doc.addEventListener('shopify:section:unload', (event) => destroy(event.target));
    doc.addEventListener('shopify:section:select', (event) => dispatchHook(event.target, 'onSectionSelect', event));
    doc.addEventListener('shopify:section:deselect', (event) => dispatchHook(event.target, 'onSectionDeselect', event));
    doc.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      // Reveal blocks that live inside collapsed <details> (briefs, dropdowns).
      const details = block.closest && block.closest('details');
      if (details && !details.open) details.open = true;
      dispatchHook(sectionOf(block), 'onBlockSelect', block, event);
    });
    doc.addEventListener('shopify:block:deselect', (event) => {
      dispatchHook(sectionOf(event.target), 'onBlockDeselect', event.target, event);
    });
  };

  /* ------------------------------------------------------- loop video */

  register('dv-loop-video', (root) => {
    const videos = queryAll(root, 'video[data-dv-loop-video]');
    if (!videos.length) return undefined;

    const states = new Map();
    const cleanups = [];

    const load = (video) => {
      let changed = false;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.getAttribute('data-src');
        source.removeAttribute('data-src');
        changed = true;
      });
      if (changed) video.load();
    };

    const play = (video, onFail) => {
      load(video);
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(onFail || (() => {}));
    };

    const sync = (video) => {
      const state = states.get(video);
      if (!state) return;
      const shouldPlay = state.visible && !doc.hidden && !state.userPaused && (!reducedMotion.matches || state.userStarted);
      if (shouldPlay) play(video);
      else video.pause();
    };

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => entries.forEach((entry) => {
        const state = states.get(entry.target);
        if (!state) return;
        state.visible = entry.isIntersecting;
        sync(entry.target);
      }), { threshold: 0.12 })
      : null;

    videos.forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.removeAttribute('autoplay');
      video.setAttribute('playsinline', '');
      const state = { visible: !observer, userPaused: false, userStarted: false };
      states.set(video, state);

      const box = video.closest('.dv-loop-media, [data-dv-loop-media]') || video.parentElement;
      const button = box ? box.querySelector('[data-dv-motion-toggle]') : null;
      const name = video.getAttribute('aria-label') || '';

      const update = () => {
        if (!button) return;
        const paused = video.paused;
        const fallback = `${paused ? 'Play' : 'Pause'} video${name ? `: ${name}` : ''}`;
        const label = button.getAttribute(paused ? 'data-dv-label-play' : 'data-dv-label-pause') || fallback;
        button.setAttribute('aria-label', label);
        const use = button.querySelector('use');
        if (use) use.setAttribute('href', paused ? '#dv-i-play' : '#dv-i-pause');
      };

      const onToggle = (event) => {
        event.preventDefault();
        if (video.paused) {
          state.userPaused = false;
          state.userStarted = true;
          play(video, update);
        } else {
          state.userPaused = true;
          video.pause();
        }
      };

      if (button) button.addEventListener('click', onToggle);
      video.addEventListener('play', update);
      video.addEventListener('pause', update);
      video.addEventListener('error', update);
      const sources = [...video.querySelectorAll('source')];
      // Every <source> failed: stop "playing" so the toggle offers Play again.
      const onSourceError = (event) => {
        if (event.target === sources[sources.length - 1]) video.pause();
        update();
      };
      sources.forEach((source) => source.addEventListener('error', onSourceError));
      update();
      if (observer) observer.observe(video);
      else sync(video);

      cleanups.push(() => {
        if (button) button.removeEventListener('click', onToggle);
        video.removeEventListener('play', update);
        video.removeEventListener('pause', update);
        video.removeEventListener('error', update);
        sources.forEach((source) => source.removeEventListener('error', onSourceError));
        video.pause();
      });
    });

    const syncAll = () => videos.forEach(sync);
    doc.addEventListener('visibilitychange', syncAll);
    const offMotion = onMediaChange(reducedMotion, syncAll);

    return () => {
      if (observer) observer.disconnect();
      doc.removeEventListener('visibilitychange', syncAll);
      offMotion();
      cleanups.forEach((fn) => fn());
      states.clear();
    };
  });

  /* ------------------------------------------------------------- start */

  const start = () => {
    if (started) return;
    started = true;
    ensureToast();
    allRoots().forEach(init);
    doc.dispatchEvent(new CustomEvent('dv:ready'));
  };

  doc.addEventListener('click', onDocumentClick);
  doc.addEventListener('keydown', onDocumentKeydown);
  doc.addEventListener('close', onDialogClosed, true); // `close` does not bubble
  window.addEventListener('scroll', onScroll, { passive: true });
  bindEditor();
  syncScroll();

  /* Klaviyo teaser guard (daile-storefront-fixes.css): body:has(<an overlay
     is open>) sets the registered, non-inherited --dv-kl-hide with a 1 ms
     transition; when it settles, html.dv-kl-hide follows it. So the page is
     restyled only when an overlay opens or closes, not on every class change. */
  if (doc.body) {
    const syncTeaser = () => {
      const value = parseFloat(getComputedStyle(doc.body).getPropertyValue('--dv-kl-hide')) || 0;
      html.classList.toggle('dv-kl-hide', value >= 0.5);
    };
    const onTeaserTransition = (event) => {
      if (event.target === doc.body && event.propertyName === '--dv-kl-hide') syncTeaser();
    };
    doc.body.addEventListener('transitionend', onTeaserTransition);
    doc.body.addEventListener('transitioncancel', onTeaserTransition);
    syncTeaser();
  }

  window.DV = {
    version: '1.0.0',
    register,
    init,
    destroy,
    openDialog,
    closeDialog,
    toast,
    queryAll,
    onMediaChange,
    reducedMotion,
    isRTL: () => (html.getAttribute('dir') || '').toLowerCase() === 'rtl',
    designMode: !!(window.Shopify && window.Shopify.designMode),
  };

  if (doc.readyState === 'complete') start();
  else {
    doc.addEventListener('DOMContentLoaded', start, { once: true });
    window.addEventListener('load', start, { once: true });
  }
})();
